/*
  RequestRabbit.js - RequestRabbit Index File

  Abstract networking module using Fetch API
*/

// CONFIGURATION
import Config from '../Config';

/*
  Makes a request on behalf of the User, based on passed in parameters.
  Inputs:
    - url: String containing the URL to send the request to
    - body: Any fields to add to the body of the request.  Should be an object
    - headers: Any headers to add to the body.  Should be an object
*/
const request = async (url, options, errorHandler) => {
  let response = null;
  try {
    if (!options || typeof options === 'undefined')
      throw new Error('Requires valid Options object');

    // Set Allow Credentials
    options.credentials = 'include';

    // Fetch
    response = await fetch(url, options);

    // If we received an error and it is to do with the web session
    if (response?.error && response?.message.includes('Web Session')) {
      // Deletes localStorage
      localStorage.removeItem('rox_user_token');
      localStorage.removeItem('rox_user_email');

      // Head back to the home page
      window.location.href = '/';
    }

    // Throws error if status code is invalid.
    if (response?.status > 399 && response?.status < 600) {
      const responseJSON = await response?.clone().json();
      throw new Error(responseJSON.error);
    }

    // Else if regular response, return as an Error
    if (response?.error) throw new Error(response?.error);

    return response;
  } catch (err) {
    console.error('req rabbit err:', err, url, options);
    if (errorHandler) errorHandler(err.message);
    else console.error('errorHandler missing for:', url);
    return response;
  }
};

const get = async (uri, queryData = {}, errorHandler = null) => {
  let url = `${Config.baseServerURL}${uri}`;

  if (Object.keys(queryData).length > 0) {
    const params = new URLSearchParams(queryData);
    url = `${url}?${params.toString()}`;
  }

  const options = {
    method: 'GET',
  };

  try {
    const response = await request(url, options, errorHandler);
    if (!response) return null;

    return _getJson(response);
  } catch (e) {
    errorHandler
      ? errorHandler(e.message)
      : console.error('errorHandler missing for:', uri, e.message);
    return null;
  }
};

const post = async (
  uri,
  data = {},
  errorHandler = null,
  returnResponse = false
) => {
  const url = `${Config.baseServerURL}${uri}`;
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  };

  try {
    const response = await request(url, options, errorHandler);

    if (returnResponse) return response;

    if (!response) return null;

    return _getJson(response);
  } catch (e) {
    errorHandler
      ? errorHandler(e.message)
      : console.error('errorHandler missing for:', uri, e.message);
    return null;
  }
};

const put = async (uri, data = {}, errorHandler = null) => {
  const url = `${Config.baseServerURL}${uri}`;
  const options = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  };

  try {
    const response = await request(url, options, errorHandler);
    if (!response) return null;

    return _getJson(response);
  } catch (e) {
    errorHandler
      ? errorHandler(e.message)
      : console.error('errorHandler missing for:', uri, e.message);
    return null;
  }
};

const del = async (uri, errorHandler = null) => {
  const url = `${Config.baseServerURL}${uri}`;
  const options = {
    method: 'DELETE',
  };

  try {
    await request(url, options, errorHandler);
  } catch (e) {
    errorHandler
      ? errorHandler(e.message)
      : console.error('errorHandler missing for:', uri, e.message);
  }
};

const _getJson = async (response) => {
  return response.headers.get('Content-Length') &&
    parseInt(response.headers.get('Content-Length')) > 0
    ? await response.json()
    : null;
};

const RequestRabbit = {
  request,
  get,
  post,
  put,
  del,
};

export default RequestRabbit;
